import React from 'react';
import { imageURL } from '../../../imageURL';

/** Must match printer settings model validator and Printer Settings UI */
export const RELEASE_A5_MONEY_LENDER = 'A5_RELEASE_MONEY_LENDER';

const resolveUploadSrc = (name) => {
  if (!name) return null;
  if (typeof name !== 'string') return null;
  if (name.startsWith('data:') || name.startsWith('http://') || name.startsWith('https://')) return name;
  let file = name.replace(/^\//, '').trim();
  if (/[\\/]/.test(file)) file = file.split(/[/\\]/).pop() || file;
  const base = (imageURL || '').replace(/\/$/, '');
  return `${base}/${file}`;
};

const formatDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleDateString('en-GB');
};

const formatMoney = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const articleDescription = (pledgeData) => {
  const arts = pledgeData?.articles || [];
  if (arts.length === 0) return '—';
  return arts
    .map((a, i) => {
      const parts = [a.description, a.type, a.grossWt || a.grossWeight].filter(Boolean);
      return parts.length ? `(${i + 1}) ${parts.join(' ')}` : null;
    })
    .filter(Boolean)
    .join('; ')
    .slice(0, 220);
};

/**
 * A5 portrait — money-lender style release receipt (inspired by printed loan repayment layout).
 */
const ReleasePrintA5MoneyLender = ({ pledgeData, releaseData, companyData }) => {
  if (!pledgeData || !releaseData) return null;

  const principal = Number(releaseData?.calculations?.principle ?? pledgeData?.loanDetails?.principalAmount) || 0;
  const interest = Number(releaseData?.calculations?.interest) || 0;
  const total = Number(releaseData?.calculations?.total) || principal + interest;

  const cust = pledgeData.customer || {};
  const nominee = cust.nomineeName || '—';
  const borrower = [cust.firstName, cust.lastName].filter(Boolean).join(' ').trim() || releaseData?.client?.name || '—';
  const ticket = pledgeData.loanDetails?.ticketNo || releaseData?.ticketNo || '—';
  const fy = releaseData.financialYear || '—';
  const rcpt = `R-${String(releaseData._id || releaseData.id || ticket).replace(/\s+/g, '').slice(-10)}`;

  const interestFrom = formatDate(pledgeData.date);
  const interestTill = formatDate(releaseData.releaseDate);

  const payRows = Array.isArray(releaseData.paymentModes) ? releaseData.paymentModes : [];
  const custImg = resolveUploadSrc(releaseData.releaseCustomerPhoto);
  const artImg = resolveUploadSrc(releaseData.releaseArticlePhoto);

  const companyName = companyData?.companyName || 'COMPANY NAME';
  const address = companyData?.address || '';
  const mobile =
    companyData?.alternateMobileNumber && companyData?.mobileNumber
      ? `${companyData.mobileNumber} / ${companyData.alternateMobileNumber}`
      : companyData?.mobileNumber || '—';
  const license = companyData?.licenseNumber || '—';
  const formRef = companyData?.maharashtraFormRef || '(Form No.9 & Law No.16)';

  return (
    <div
      className="release-print-a5-money-lender"
      style={{
        width: '100%',
        maxWidth: '148mm',
        minHeight: '200mm',
        margin: '0 auto',
        padding: '3mm 4mm',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '10px',
        color: '#111',
        lineHeight: 1.35,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '4px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700 }}>{companyName}</div>
        <div style={{ fontSize: '9px', marginTop: '2px' }}>{address}</div>
        <div style={{ fontSize: '9px', marginTop: '2px' }}>Tel. No.: {mobile}</div>
        <div style={{ fontSize: '8.5px', marginTop: '2px' }}>
          {formRef} Money Lendor Licence No. : {license}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '9px' }}>
        <div>
          <strong>Rcpt No.</strong> {rcpt}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div>
            <strong>Date:</strong> {formatDate(releaseData.releaseDate)}
          </div>
          <div>
            <strong>Year:</strong> {fy}
          </div>
        </div>
      </div>

      <div style={{ marginTop: '8px', borderTop: '1px solid #333', paddingTop: '6px', fontSize: '9.5px' }}>
        <div>
          <strong>Borrower:</strong> {borrower.toUpperCase()}
        </div>
        <div style={{ marginTop: '2px' }}>
          <strong>Nominee:</strong> {nominee}
        </div>
        <div style={{ marginTop: '2px' }}>
          <strong>Against Bill:</strong> {ticket}
        </div>
        <div style={{ marginTop: '2px' }}>
          <strong>Description Of Goods:</strong> {articleDescription(pledgeData)}
        </div>
        <div style={{ marginTop: '4px' }}>
          <strong>Interest From:</strong> {interestFrom}{' '}
          <span style={{ marginLeft: '12px' }}>
            <strong>Interest Till:</strong> {interestTill}
          </span>
        </div>
      </div>

      <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '9.5px', minWidth: '52%' }}>
          <tbody>
            <tr>
              <td style={{ padding: '2px 6px', textAlign: 'right' }}>
                <strong>Capital Amount (Rs.):</strong>
              </td>
              <td style={{ padding: '2px 6px', textAlign: 'right', borderBottom: '1px solid #999' }}>
                {formatMoney(principal)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '2px 6px', textAlign: 'right' }}>
                <strong>Interest (Rs.):</strong>
              </td>
              <td style={{ padding: '2px 6px', textAlign: 'right', borderBottom: '1px solid #999' }}>
                {formatMoney(interest)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                <strong>Total Amount (Rs.):</strong>
              </td>
              <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700 }}>{formatMoney(total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            fontSize: '9px',
            flex: '1 1 55%',
            border: '1px solid #333',
          }}
        >
          <thead>
            <tr>
              <th style={{ border: '1px solid #333', padding: '3px' }}>#</th>
              <th style={{ border: '1px solid #333', padding: '3px' }}>PAY TYPE</th>
              <th style={{ border: '1px solid #333', padding: '3px' }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {(payRows.length ? payRows : [{ type: 'cash', amount: total }]).map((row, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid #333', padding: '3px', textAlign: 'center' }}>{i + 1}</td>
                <td style={{ border: '1px solid #333', padding: '3px' }}>{String(row.type || 'cash').toUpperCase()}</td>
                <td style={{ border: '1px solid #333', padding: '3px', textAlign: 'right' }}>
                  {Number(row.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ flex: '0 0 38%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {custImg ? (
            <img
              src={custImg}
              alt="Customer"
              style={{ width: '100%', maxHeight: '52mm', objectFit: 'contain', border: '1px solid #999' }}
            />
          ) : null}
          {artImg ? (
            <img
              src={artImg}
              alt="Article"
              style={{ width: '100%', maxHeight: '52mm', objectFit: 'contain', border: '1px solid #999' }}
            />
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'space-between', fontSize: '9px' }}>
        <div>
          <div style={{ borderTop: '1px solid #333', paddingTop: '4px', minWidth: '110px' }}>Sign of Borrower</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ borderTop: '1px solid #333', paddingTop: '4px', minWidth: '110px', display: 'inline-block' }}>
            Sign of Lender
          </div>
        </div>
      </div>

      <div style={{ marginTop: '10px', textAlign: 'center', fontSize: '7.5px', color: '#555' }}>
        {companyData?.printFooter?.trim() ||
          'Powered by Prime Software Solution (www.prime.co.in)'}
      </div>
    </div>
  );
};

export default ReleasePrintA5MoneyLender;
